import { SUBJECT_CATEGORY_CODES, type SubjectCategoryCode } from '@athena/shared'
import { useQuery } from '@apollo/client/react'
import {
  Button,
  Checkbox,
  ConfirmationDialog,
  ErrorState,
  FieldMessage,
  LoadingState,
  PageLayout,
  Section,
  TextInput,
  useTranslate,
} from '@athena/ui'
import { useBlocker } from '@tanstack/react-router'
import { useRef, useState } from 'react'

import { usePageTitle } from '../app/page-title.js'
import {
  subjectMatchesCategories,
  useSubjectPreferences,
} from '../features/practices/useSubjectPreferences.js'
import { AllSubjectsDocument } from '../graphql/generated/operations.js'

export function PracticeSettingsScreen() {
  const t = useTranslate()
  usePageTitle(t('practice.preferences.title'))
  const catalog = useQuery(AllSubjectsDocument, { fetchPolicy: 'cache-and-network' })
  const preferences = useSubjectPreferences()
  // A draft belongs to its account even if the session changes while mounted.
  const [draft, setDraft] = useState<{
    userId: string
    hidden: string[]
    categories: SubjectCategoryCode[]
  } | null>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState<'idle' | 'saved' | 'failed'>('idle')
  const hidden = draft && draft.userId === preferences.userId ? draft.hidden : preferences.hiddenIds
  const categories =
    draft && draft.userId === preferences.userId ? draft.categories : preferences.categoryCodes
  const dirty = Boolean(
    draft &&
    draft.userId === preferences.userId &&
    (categories.length !== preferences.categoryCodes.length ||
      categories.some((code) => !preferences.categoryCodes.includes(code)) ||
      hidden.length !== preferences.hiddenIds.length ||
      hidden.some((id) => !preferences.hiddenIds.includes(id))),
  )
  const blocker = useBlocker({
    shouldBlockFn: () => dirty,
    enableBeforeUnload: dirty,
    withResolver: true,
  })
  const subjects = catalog.data?.allSubjects ?? []
  const visibleCount = subjects.filter(
    (subject) => !hidden.includes(subject.id) && subjectMatchesCategories(subject, categories),
  ).length
  const normalize = (text: string) =>
    text.normalize('NFD').replace(/\p{M}/gu, '').toLocaleLowerCase().trim()
  const matches = subjects.filter((subject) => normalize(subject.name).includes(normalize(search)))
  // Each subject is listed once, under the first category in catalog order that
  // offers it — so Secondary does not repeat what Primary already shows. The
  // first *enabled* one wins, so a shared subject still offered through another
  // category never sits greyed out inside a switched-off section.
  const homeOf = (subject: (typeof subjects)[number]) => {
    const codes = subject.categoryCodes ?? []
    return (
      SUBJECT_CATEGORY_CODES.find((code) => codes.includes(code) && categories.includes(code)) ??
      SUBJECT_CATEGORY_CODES.find((code) => codes.includes(code)) ??
      'UNMAPPED'
    )
  }
  function update(next: string[], nextCategories = categories) {
    if (!preferences.userId) return
    setDraft({ userId: preferences.userId, hidden: next, categories: nextCategories })
    setStatus('idle')
  }
  return (
    <PageLayout width="operational">
      <Section id="practice-subject-preferences" title={t('practice.preferences.subjectsTitle')}>
        <p className="max-w-2xl text-body-sm text-text-secondary">
          {t('practice.preferences.description')}
        </p>
        {catalog.loading || preferences.loading ? (
          <LoadingState>{t('common.loading')}</LoadingState>
        ) : catalog.error || preferences.error ? (
          <ErrorState>{t('practice.preferences.loadError')}</ErrorState>
        ) : (
          <>
            <div className="flex flex-wrap items-end gap-3">
              <div className="min-w-0 flex-1">
                <TextInput
                  ref={searchRef}
                  label={t('practice.preferences.search')}
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
              </div>
              {search ? (
                <Button
                  variant="secondary"
                  onClick={() => {
                    setSearch('')
                    searchRef.current?.focus()
                  }}
                >
                  {t('common.clear')}
                </Button>
              ) : null}
              <Button variant="secondary" onClick={() => update([], [...SUBJECT_CATEGORY_CODES])}>
                {t('practice.preferences.all')}
              </Button>
            </div>
            <p className="text-body-sm text-text-secondary">
              {t('practice.preferences.selectionSummary', [visibleCount, subjects.length])}
            </p>
            <div className="grid gap-4 desktop:grid-cols-2">
              {[...SUBJECT_CATEGORY_CODES, 'UNMAPPED' as const].map((code) => {
                const members = matches.filter((subject) => homeOf(subject) === code)
                if (code === 'UNMAPPED' && members.length === 0) return null
                const enabled = code === 'UNMAPPED' || categories.includes(code)
                const heading =
                  code === 'UNMAPPED'
                    ? t('subjectCategories.other')
                    : t(`subjectCategories.names.${code}`)
                return (
                  <section
                    key={code}
                    aria-label={heading}
                    className="rounded-md border border-border bg-surface"
                  >
                    <div className="flex items-center justify-between gap-3 border-b border-border px-3 py-2">
                      {code === 'UNMAPPED' ? (
                        <h2 className="text-body font-semibold text-text">{heading}</h2>
                      ) : (
                        <Checkbox
                          density="compact"
                          label={<span className="font-semibold">{heading}</span>}
                          checked={enabled}
                          onCheckedChange={(checked) =>
                            update(
                              hidden,
                              checked === true
                                ? [...categories, code]
                                : categories.filter((value) => value !== code),
                            )
                          }
                        />
                      )}
                      <span className="text-body-sm text-text-secondary">
                        {t(
                          members.length === 1
                            ? 'subjectCategories.countOne'
                            : 'subjectCategories.countOther',
                          [members.length],
                        )}
                      </span>
                    </div>
                    <div className="grid max-h-72 gap-x-4 overflow-y-auto p-3 sm:grid-cols-2">
                      {members.map((subject) => (
                        <Checkbox
                          key={subject.id}
                          density="compact"
                          label={
                            <span className={enabled ? 'text-text' : 'text-text-disabled'}>
                              {subject.name}
                            </span>
                          }
                          disabled={!enabled}
                          checked={enabled && !hidden.includes(subject.id)}
                          onCheckedChange={(checked) =>
                            update(
                              checked === true
                                ? hidden.filter((id) => id !== subject.id)
                                : [...hidden, subject.id],
                            )
                          }
                        />
                      ))}
                      {members.length === 0 ? (
                        <p className="text-body-sm text-text-secondary">
                          {t('practice.preferences.noResults')}
                        </p>
                      ) : null}
                    </div>
                  </section>
                )
              })}
            </div>
            {visibleCount === 0 ? (
              <FieldMessage tone="error">{t('practice.preferences.required')}</FieldMessage>
            ) : null}
            <div className="flex flex-wrap items-center gap-3">
              <Button
                disabled={visibleCount === 0 || !preferences.userId}
                onClick={() => setStatus(preferences.save(hidden, categories) ? 'saved' : 'failed')}
              >
                {t('practice.preferences.save')}
              </Button>
              {status === 'saved' ? (
                <p role="status" className="text-body-sm text-success-text">
                  {t('practice.preferences.saved')}
                </p>
              ) : null}
              {status === 'failed' ? (
                <FieldMessage tone="error">{t('practice.preferences.saveError')}</FieldMessage>
              ) : null}
            </div>
          </>
        )}
      </Section>
      <ConfirmationDialog
        open={blocker.status === 'blocked'}
        intent="caution"
        title={t('practice.preferences.unsavedTitle')}
        description={t('practice.preferences.unsavedBody')}
        confirmLabel={t('practice.preferences.discard')}
        cancelLabel={t('practice.preferences.keepEditing')}
        onOpenChange={(open) => {
          if (!open && blocker.status === 'blocked') blocker.reset()
        }}
        onConfirm={() => {
          if (blocker.status === 'blocked') blocker.proceed()
        }}
      />
    </PageLayout>
  )
}
